import { capitalize, useScrollBlock } from '@repo-level/core-front';
import { ButtonCross } from 'components/buttons';
import { motion } from 'framer-motion';
import type React from 'react';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ScModalSelectBodyProps } from './styled';
import {
  ScModalSelect,
  ScModalSelectBody,
  ScModalSelectHeader,
  ScModalSelectInner,
  ScModalSelectOption,
  ScSelectSearchMob,
} from './styled';

type Option = {
  icon?: string;
  name: React.ReactNode;
  nameS?: string;
  nameV?: string;
  value: string;
  disabled?: boolean;
  selected?: boolean;
};

interface ModalSelectProps {
  title: string;
  icon?: JSX.Element;
  options: Option[];
  borderRoundIconOption?: boolean;
  onClose: () => void;
  onChange?: (value: Option['value']) => void;
  search?: boolean;
  selectedOption?: string | number;
}

export const ModalSelect: FC<ModalSelectProps & ScModalSelectBodyProps> = ({
  options,
  borderRoundIconOption = false,
  onChange,
  search,
  selectedOption,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');

  const handleChangeSearchValue = useCallback(
    (value: string) => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const filteredOptions = useMemo(() => {
    if (searchValue === '' || search == null) return options;

    return options.filter(({ nameS }) => {
      if (nameS == null) return true;
      return nameS.toLowerCase().includes(searchValue.toLowerCase());
    });
  }, [searchValue, options, search]);

  return (
    <ModalSelectContainer
      onChangeSearch={handleChangeSearchValue}
      search={search}
      {...props}
    >
      <ScModalSelectInner>
        {filteredOptions.length === 0 && (
          <div className="not-found">Not found</div>
        )}
        {filteredOptions.map((option, index) => (
          <OptionItem
            key={index}
            {...option}
            borderRoundIconOption={borderRoundIconOption}
            onChange={onChange}
            selected={selectedOption === option.value}
          />
        ))}
      </ScModalSelectInner>
    </ModalSelectContainer>
  );
};

interface ModalSelectContainerProps {
  title: string;
  icon?: JSX.Element;
  search?: boolean;
  addonsBefore?: JSX.Element;
  addonsAfter?: JSX.Element;
  onClose: () => void;
  onChangeSearch?: (value: string) => void;
}

export const ModalSelectContainer: FC<
  ModalSelectContainerProps & ScModalSelectBodyProps
> = ({
  title,
  icon,
  search,
  addonsBefore,
  addonsAfter,
  onClose,
  onChangeSearch,
  children,

  ...props
}) => {
  useScrollBlock();

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current == null || search == null) return;

    ref.current.focus();
  }, [search]);

  const handleChangeSearchValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeSearch && onChangeSearch(e.target.value);
    },
    [onChangeSearch]
  );

  return (
    <ScModalSelect
      animate={{ y: 0 }}
      as={motion.div}
      exit={{ y: '100%' }}
      initial={{ y: '100%' }}
      transition={{ duration: 0.2 }}
      {...props}
    >
      <ScModalSelectHeader>
        <div className="header">
          {icon && <div className="icon">{icon}</div>}
          <div className="title">{capitalize(title)}</div>
        </div>
        <ButtonCross onClick={onClose} right={12} square top={12} />
      </ScModalSelectHeader>
      {addonsBefore}
      <ScModalSelectBody borderBottom={!!addonsAfter} {...props}>
        {search && (
          <ScSelectSearchMob>
            <input
              onChange={handleChangeSearchValue}
              placeholder="Search..."
              ref={ref}
              type="text"
            />
            <div className="divider" />
          </ScSelectSearchMob>
        )}
        {children}
      </ScModalSelectBody>
      {addonsAfter}
    </ScModalSelect>
  );
};

const OptionItem: FC<
  Pick<ModalSelectProps, 'borderRoundIconOption' | 'onChange'> & Option
> = ({ name, value, icon, borderRoundIconOption, selected, onChange }) => {
  const handleClick = () => {
    if (onChange == null) return;
    onChange(value);
  };

  return (
    <ScModalSelectOption
      onClick={handleClick}
      roundIcon={borderRoundIconOption}
      selected={selected}
    >
      {icon && (
        <div className="icon">
          <img alt="" src={icon} />
        </div>
      )}
      <div className="name">{name}</div>
    </ScModalSelectOption>
  );
};
